import { token } from '@atlaskit/tokens';
import { useFlaggingCallback } from '@databases/banners';
import { useDatabaseContent } from '@databases/database-state';
import EllipsisOverflow from '@databases/field-value/common/EllipsisOverflow';
import { useCallbackEffect } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import { KnownUser } from '@databases/types/confluence';
import { selectCurrentUserId } from '@databases/utils/redux/context/selectors';
import { loadUserById } from '@databases/utils/redux/users/actions';
import { selectUserByIdOptional } from '@databases/utils/redux/users/selectors';
import { isEmbed } from '@databases/utils/utils';
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';

import { AvatarWithLabel } from './AvatarWithLabel';

const MentionContainer = styled.span<{ isCurrentUser: boolean; isValueEmbed: boolean }>`
  display: inline-flex;
  background: ${(props) =>
    props.isCurrentUser
      ? token('color.background.brand.bold', '#0C66E4')
      : token('color.background.neutral', '#091E420F')};
  color: ${(props) =>
    props.isCurrentUser
      ? token('color.text.inverse', '#FFFFFF')
      : token('color.text.subtle', '#44546F')};
  border-radius: 20px;
  padding: 0 0.5em 0px 0.5em;
  white-space: nowrap;
  max-width: calc(100% - 10px);
`;

export const renderUserName: NonNullable<UserNameProps['renderUser']> = ({ user }) => {
  return (
    <span>
      <EllipsisOverflow>{user.displayName}</EllipsisOverflow>
    </span>
  );
};

export const renderUserMention: NonNullable<UserNameProps['renderUser']> = ({
  user,
  isCurrentUser,
  isValueEmbed,
}) => {
  return (
    <MentionContainer
      isCurrentUser={isCurrentUser}
      isValueEmbed={isValueEmbed}
      data-mention-id={user.accountId}
    >
      <EllipsisOverflow>
        {
          // Won't translate @
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
        }
        @{user.displayName}
      </EllipsisOverflow>
    </MentionContainer>
  );
};

export const renderUserWithAvatar: NonNullable<UserNameProps['renderUser']> = ({
  user,
}) => {
  return (
    <AvatarWithLabel
      userOrGroup={'profilePicture' in user ? user : undefined}
      label={user.displayName}
    />
  );
};

interface UserNameProps {
  accountId: string;
  /** If specified, will be shown as the display name until the account is loaded. */
  displayName?: string;
  renderUser?: (data: {
    user: KnownUser | { accountId: string; displayName: string };
    isCurrentUser: boolean;
    isValueEmbed: boolean;
  }) => ReactNode;
}

/**
 * Shows the username of a user with the specified accountId. Uses the REST API to load the user details and
 * stores them in the redux store.
 */
export const UserName: FunctionComponent<UserNameProps> = ({
  accountId,
  displayName,
  renderUser = renderUserName,
}) => {
  const [user, isCurrentUser] = defaultStoreUtils.useEqualSelector((state) => {
    const currentUserId = selectCurrentUserId(state);
    const user = selectUserByIdOptional(state, { accountId });
    return [
      user ?? (displayName != null ? { accountId, displayName } : undefined),
      !!currentUserId && accountId === currentUserId,
    ] as const;
  });

  const dispatch = defaultStoreUtils.useDispatch();
  useCallbackEffect(
    useFlaggingCallback(async () => {
      await dispatch(loadUserById(accountId));
    }, [accountId, dispatch])
  );

  const { contentId, entryId, fieldId } = useDatabaseContent();
  const isValueEmbed = isEmbed() && !!contentId && !!entryId && !!fieldId;

  if (!user) {
    return null;
  } else {
    return (
      <span data-testid="user-mention">
        {renderUser({ user, isCurrentUser, isValueEmbed })}
      </span>
    );
  }
};
